import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { getRoomMismatch, parseRoomFilters, type RoomMismatch } from "./roomMismatch";
export type RoomRailItem = {
  slug: string;
  title: string;
  rentPrice?: string | null;
  thumbnail?: string | null;
  gender?: string | null;
  availableFrom?: string | null;
  petFriendly?: boolean | null;
  contract?: boolean | null;
  smokingAllowed?: boolean | null;
  // false → doesn't match the active search filters; dimmed and hidden behind a toggle.
  matchesFilters?: boolean;
};

// Reusable across the rail (per room) and the detail banner (current room):
// turns a room's attributes + the active filters into a human reason string.
export function useRoomReason(filterParams) {
  const $ = _c(6);
  const t = useTranslations("modules.realEstateDetail");
  const locale = useLocale();
  let t0;
  if ($[0] !== filterParams) {
    t0 = parseRoomFilters(filterParams);
    $[0] = filterParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const filters = t0;
  let t1;
  if ($[2] !== filters || $[3] !== locale || $[4] !== t) {
    t1 = room => mismatchToText(getRoomMismatch(room, filters), t, locale);
    $[2] = filters;
    $[3] = locale;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function useRoomRail(t0) {
  const $ = _c(13);
  const {
    rooms,
    activeSlug,
    filterParams
  } = t0;
  const [showOthers, setShowOthers] = useState(false);
  const reasonText = useRoomReason(filterParams);
  let t1;
  if ($[0] !== rooms) {
    t1 = rooms.filter(_temp);
    $[0] = rooms;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const matching = t1;
  let nonMatching;
  let t2;
  if ($[2] !== activeSlug || $[3] !== rooms || $[4] !== showOthers) {
    nonMatching = rooms.filter(_temp2);
    t2 = showOthers ? nonMatching : nonMatching.filter(room_1 => room_1.slug === activeSlug);
    $[2] = activeSlug;
    $[3] = rooms;
    $[4] = showOthers;
    $[5] = nonMatching;
    $[6] = t2;
  } else {
    nonMatching = $[5];
    t2 = $[6];
  }
  const visibleNonMatching = t2;
  const hiddenCount = nonMatching.length - visibleNonMatching.length;
  let t3;
  if ($[7] !== hiddenCount || $[8] !== matching || $[9] !== reasonText || $[10] !== showOthers || $[11] !== visibleNonMatching) {
    t3 = {
      matching,
      visibleNonMatching,
      hiddenCount,
      showOthers,
      setShowOthers,
      reasonText
    };
    $[7] = hiddenCount;
    $[8] = matching;
    $[9] = reasonText;
    $[10] = showOthers;
    $[11] = visibleNonMatching;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function _temp2(room_0) {
  return room_0.matchesFilters === false;
}
function _temp(room) {
  return room.matchesFilters !== false;
}
const AVAILABILITY_PRESETS = ["now", "1m", "2m", "6m", "1y"];
function formatDate(iso: string, locale: string) {
  return new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
}
function availabilityFilterLabel(filter: string, t: ReturnType<typeof useTranslations>, locale: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(filter)) {
    return t("availabilityFilter.date", {
      date: formatDate(filter, locale)
    });
  }
  return AVAILABILITY_PRESETS.includes(filter) ? t(`availabilityFilter.${filter}`) : t("availabilityFilter.generic");
}
function mismatchToText(mismatch: RoomMismatch, t: ReturnType<typeof useTranslations>, locale: string): string {
  const price = (value: number) => `${value.toLocaleString("pt-PT")}€`;
  switch (mismatch.type) {
    case "priceAbove":
      return t("roomReasonPriceAbove", {
        price: price(mismatch.price),
        max: price(mismatch.max)
      });
    case "priceBelow":
      return t("roomReasonPriceBelow", {
        price: price(mismatch.price),
        min: price(mismatch.min)
      });
    case "gender":
      return t("roomReasonGender");
    case "petFriendly":
      return t("roomReasonPetFriendly");
    case "contract":
      return t("roomReasonContract");
    case "smokingAllowed":
      return t("roomReasonSmoking");
    case "availability":
      {
        const filter = availabilityFilterLabel(mismatch.filter, t, locale);
        return mismatch.availableFrom ? t("roomReasonAvailability", {
          date: formatDate(mismatch.availableFrom, locale),
          filter
        }) : t("roomReasonAvailabilityNow", {
          filter
        });
      }
    default:
      return t("roomOutsideFiltersGeneric");
  }
}